import { create } from 'zustand'
import steamTopupApi from '../services/steamTopupApi'
import useAppStore from '../../../store/useAppStore'

/**
 * Withdraw Store
 * Zustand store для управления состоянием вывода кристаллов
 *
 * Поддерживает поллинг активной заявки (каждые 12 секунд),
 * пока статус не станет финальным (success / cancelled / stuck)
 */

const POLLING_INTERVAL = 12000 // 12 секунд

const useWithdrawStore = create((set, get) => ({
  // Данные экрана из GET /api/steam-topup/
  info: null,         // { enabled, can_withdraw, has_active, crystals_per_dollar_net, min_crystals, max_crystals, last_steam_login }
  history: [],        // последние 50 заявок

  // Состояния загрузки
  isLoading: false,
  isSubmitting: false,
  error: null,

  // ID таймера поллинга
  _pollingTimer: null,

  /**
   * Загрузить данные экрана вывода (info + history)
   */
  loadData: async () => {
    set({ isLoading: true, error: null })

    try {
      const data = await steamTopupApi.fetchSteamTopupData()
      set({
        info: data.info || null,
        history: data.history || [],
        isLoading: false,
      })

      // Если есть активная заявка — запускаем поллинг
      if (data.info?.has_active) {
        get().startPolling()
      }

      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки данных вывода:', error)
      set({
        isLoading: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Создать заявку на вывод в Steam
   * @param {string} steamLogin — логин Steam
   * @param {number} crystals — сумма в кристаллах
   * @returns {Promise<Object>} результат { id, usd, crystals, status }
   */
  submitSteamTopup: async (steamLogin, crystals) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await steamTopupApi.createSteamTopup(steamLogin, crystals)
      set({ isSubmitting: false })

      // Перезагружаем данные чтобы получить обновлённую историю и has_active
      await get().loadData()

      return result
    } catch (error) {
      console.error('❌ Ошибка создания заявки вывода:', error)
      set({
        isSubmitting: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Запустить поллинг статуса активной заявки
   */
  startPolling: () => {
    // Останавливаем предыдущий таймер если был
    get().stopPolling()

    const timer = setInterval(async () => {
      const { info } = get()

      // Если нет активной заявки — останавливаем поллинг
      if (!info?.has_active) {
        get().stopPolling()
        return
      }

      try {
        const data = await steamTopupApi.fetchSteamTopupData()
        set({
          info: data.info || null,
          history: data.history || [],
        })

        // Если активная заявка перешла в финальный статус — останавливаем поллинг и обновляем баланс
        if (!data.info?.has_active) {
          get().stopPolling()
          // Обновляем баланс — при cancel кристаллы возвращаются
          useAppStore.getState().loadProfile?.()
        }
      } catch (error) {
        console.error('❌ Ошибка поллинга вывода:', error)
        // При ошибке не останавливаем поллинг — попробуем снова
      }
    }, POLLING_INTERVAL)

    set({ _pollingTimer: timer })
  },

  /**
   * Остановить поллинг
   */
  stopPolling: () => {
    const { _pollingTimer } = get()
    if (_pollingTimer) {
      clearInterval(_pollingTimer)
      set({ _pollingTimer: null })
    }
  },

  /**
   * Сбросить состояние store
   */
  reset: () => {
    get().stopPolling()
    set({
      info: null,
      history: [],
      isLoading: false,
      isSubmitting: false,
      error: null,
    })
  },
}))

export default useWithdrawStore
